import React, { useEffect, useState } from "react";
import { freight } from "../../lib/freightApi.js";
import { useCarrier } from "./CarrierApp.jsx";
import { Shell, Back, Title, Card, Stat, Select, Button, Field, Note, Quiet, C, errText } from "./ui.jsx";
import { money, count } from "./reportsView.js";

/**
 * Savings (the schematic's Settings · Savings board): the phone work Alfred did, at your team's own
 * minutes per task and hourly cost.
 *
 * ⛔ ONLY CALLS A PERSON ANSWERED COUNT (backend reports.mjs): a voicemail, a phone menu or a call that
 * never went out saved nobody any time. Seeded and AI-persona calls are left out. The minutes and the
 * hourly cost are the carrier's own, keyed in from the setup email or changed here; until both are
 * in, the page shows the calls and no dollar figure. Detention is not here: nothing logs it yet.
 */

const PERIODS = [{ v: "this_month", label: "This month" }, { v: "last_month", label: "Last month" }, { v: "90", label: "Last 90 days" }];
const TASKS = [
  { key: "checkCall", label: "Check calls" }, { key: "loadOffer", label: "Load offers" },
  { key: "applicantScreen", label: "Applicant screens" }, { key: "afterHours", label: "After-hours calls" },
];
const th = { textAlign: "left", fontSize: 14, fontWeight: 600, color: C.muted, padding: "0 16px 10px 0", borderBottom: `1px solid ${C.line}` };
/** "17:00" → "5 PM", "08:30" → "8:30 AM". */
const hhmm = (t) => { const [h, m] = String(t).split(":").map(Number); return `${((h + 11) % 12) + 1}${m ? `:${String(m).padStart(2, "0")}` : ""} ${h < 12 ? "AM" : "PM"}`; };
const td = { padding: "14px 16px 14px 0", borderBottom: `1px solid ${C.soft}`, verticalAlign: "top", textAlign: "left" };

export function InputsForm({ inputs, onSave, onCancel, busy }) {
  const [f, setF] = useState({ hourly: inputs.hourlyCents != null ? String(inputs.hourlyCents / 100) : "",
    ...Object.fromEntries(TASKS.map((t) => [t.key, inputs.minutes?.[t.key] != null ? String(inputs.minutes[t.key]) : ""])),
    officeStart: inputs.office?.start || "08:00", officeEnd: inputs.office?.end || "17:00" });
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const save = () => onSave({
    hourlyCents: f.hourly === "" ? null : Math.round(Number(f.hourly) * 100),
    minutes: Object.fromEntries(TASKS.map((t) => [t.key, f[t.key] === "" ? null : Number(f[t.key])])),
    office: { start: f.officeStart, end: f.officeEnd },
  });
  return (
    <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
      <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Your team's time</h2>
      <div style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
        <Field id="sv-hourly" label="Hourly cost, with benefits ($)" type="number" inputMode="decimal" value={f.hourly} onChange={set("hourly")} />
        {TASKS.map((t) => <Field key={t.key} id={`sv-${t.key}`} label={`Minutes for one ${t.label.toLowerCase().replace(/s$/, "")}`} type="number" inputMode="numeric" value={f[t.key]} onChange={set(t.key)} />)}
        <Field id="sv-start" label="Office opens" type="time" value={f.officeStart} onChange={set("officeStart")} />
        <Field id="sv-end" label="Office closes" type="time" value={f.officeEnd} onChange={set("officeEnd")} />
      </div>
      <div style={{ display: "flex", gap: 12 }}>
        <Button disabled={busy} onClick={save}>Save</Button>
        <Button kind="secondary" disabled={busy} onClick={onCancel}>Cancel</Button>
      </div>
    </Card>
  );
}

export function SavingsView({ company, stage, me, period, setPeriod, data, error, editing, setEditing, busy, msg, onSave }) {
  const rows = data?.rows || [];
  const ready = data && data.inputs.hourlyCents != null && TASKS.every((t) => data.inputs.minutes?.[t.key] != null);
  const label = PERIODS.find((x) => x.v === period)?.label.toLowerCase() || "";
  return (
    <Shell company={company} stage={stage}>
      <Back to="/carrier/settings">Settings</Back>
      <Title sub="How much time and money Alfred saves you.">Savings</Title>
      <Select id="sv-period" label="Period" value={period} onChange={setPeriod} options={PERIODS} width={240} />
      {error && <Quiet>{`The numbers could not be read: ${error}`}</Quiet>}
      {!data && !error && <Quiet>Loading…</Quiet>}
      {data && (
        <>
          {ready ? (
            <Card style={{ padding: 28, display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 16, color: C.muted }}>{`Saved ${label}`}</span>
              <span style={{ fontSize: 52, lineHeight: 1.1, fontWeight: 700, color: C.accent }}>{money(data.total.savedCents)}</span>
              <span style={{ fontSize: 16, color: C.muted }}>{`Phone work your team didn't have to do, at your cost of ${money(data.inputs.hourlyCents)} an hour.`}</span>
              {data.planPriceCents != null && <span style={{ fontSize: 15, color: C.muted }}>{`Your plan is ${money(data.planPriceCents)} a month.`}</span>}
            </Card>
          ) : (
            <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 10 }}>
              <span style={{ fontSize: 17 }}>Add your team's hourly cost and its minutes for each task to see what Alfred saved.</span>
              {me?.admin && <div><Button onClick={() => setEditing(true)}>Add them</Button></div>}
            </Card>
          )}
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <Stat label="Calls Alfred handled" value={count(data.total.calls)} sub="A person answered each one" />
            {ready && <Stat label="Hours your team got back" value={count(Math.round(data.total.hours))} />}
          </div>
          {editing && <InputsForm inputs={data.inputs} busy={busy} onCancel={() => setEditing(false)} onSave={onSave} />}
          <Note msg={msg} />
          <Card style={{ padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Where the time came from</h2>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", minWidth: 520, borderCollapse: "collapse", fontSize: 16 }}>
                <thead><tr>
                  <th scope="col" style={th}>Work Alfred did</th><th scope="col" style={th}>Calls</th>
                  <th scope="col" style={th}>Your team's minutes each</th><th scope="col" style={th}>Hours</th><th scope="col" style={th}>Saved</th>
                </tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.key}>
                      <th scope="row" style={{ ...td, fontWeight: 600 }}>{TASKS.find((t) => t.key === r.key)?.label || r.key}</th>
                      <td style={td}>{count(r.calls)}</td>
                      <td style={td}>{r.minutes == null ? "Not set" : r.minutes}</td>
                      <td style={td}>{r.hours == null ? "" : count(Math.round(r.hours))}</td>
                      <td style={td}>{r.savedCents == null ? "" : money(r.savedCents)}</td>
                    </tr>
                  ))}
                  <tr>
                    <th scope="row" style={{ ...td, fontWeight: 600, borderBottom: "none" }}>Total</th>
                    <td style={{ ...td, fontWeight: 600, borderBottom: "none" }}>{count(data.total.calls)}</td>
                    <td style={{ ...td, borderBottom: "none" }} />
                    <td style={{ ...td, fontWeight: 600, borderBottom: "none" }}>{ready ? count(Math.round(data.total.hours)) : ""}</td>
                    <td style={{ ...td, fontWeight: 600, borderBottom: "none" }}>{ready ? money(data.total.savedCents) : ""}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p style={{ margin: 0, fontSize: 14, color: C.muted }}>
              {data.inputs.hourlyCents != null ? `At ${money(data.inputs.hourlyCents)} an hour, with your team's minutes for each task. ` : ""}
              {data.inputs.office.set === false
                ? `After hours means outside ${hhmm(data.inputs.office.start)} to ${hhmm(data.inputs.office.end)} at your office, until you set your office hours. `
                : `After hours means outside ${hhmm(data.inputs.office.start)} to ${hhmm(data.inputs.office.end)} at your office. `}
              {me?.admin && !editing && <Button kind="quiet" onClick={() => setEditing(true)} style={{ padding: 0, minHeight: 32 }}>Edit</Button>}
            </p>
          </Card>
        </>
      )}
    </Shell>
  );
}

export default function Savings() {
  const { carrier, me } = useCarrier();
  const [period, setPeriod] = useState("this_month");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  useEffect(() => {
    let alive = true;
    setData(null); setError(null);
    freight.savings(period).then((d) => alive && setData(d)).catch((e) => alive && setError(errText(e)));
    return () => { alive = false; };
  }, [period]);
  const onSave = async (inputs) => {
    setBusy(true); setMsg(null);
    try { await freight.saveSavingsInputs(inputs); setData(await freight.savings(period)); setEditing(false); setMsg({ ok: true, text: "Saved." }); }
    catch (e) { setMsg({ ok: false, text: errText(e) }); }
    finally { setBusy(false); }
  };
  return <SavingsView company={carrier.name} stage={carrier.stage} me={me} period={period} setPeriod={setPeriod} data={data} error={error}
    editing={editing} setEditing={setEditing} busy={busy} msg={msg} onSave={onSave} />;
}
